/**
 * Клиент REST API Kaznacheistvo.
 *
 * Flow: компонент вызывает функцию → добавляется заголовок с токеном →
 *       запрос уходит на /api/* → ошибка приводится к единому виду {detail}.
 *
 * Зависимости: только fetch браузера — обёртка над HTTP не нуждается в
 * библиотеке, а лишняя зависимость во фронтенде тоже требует сопровождения.
 *
 * Связанные файлы:
 *   - src/App.jsx и блоки разделов (src/*Panel.jsx) — потребители;
 *   - docs/interface/openapi.json — контракт, который здесь используется.
 */

const TOKEN_KEY = "kaznacheistvo.token";

/** Возвращает сохранённый access-токен либо null. */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/** Сохраняет токен; null очищает сессию. */
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/**
 * Выполняет запрос к API.
 *
 * @param {string} path — путь, начиная с /api
 * @param {object} options — { method, body }
 * @returns {Promise<object>} разобранный JSON ответа
 * @throws {Error} с текстом из поля detail — единый формат ошибки (IR-005)
 *
 * Побочный эффект: при 401 токен удаляется, иначе приложение осталось бы в
 * состоянии «вошёл», в котором каждый запрос отвергается.
 */
async function request(path, { method = "GET", body } = {}) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401) {
    setToken(null);
  }

  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  if (!response.ok) {
    throw new Error(data.detail || `Ошибка ${response.status}`);
  }
  return data;
}

/** Вход по логину и паролю; возвращает { access_token, expires_at, user }. */
export function login(username, password) {
  return request("/api/auth/login", {
    method: "POST",
    body: { username, password },
  });
}

/**
 * Текущий пользователь по сохранённому токену.
 *
 * @returns {Promise<{id: number, username: string, full_name: string,
 *                    role: string, position: string, department: string,
 *                    sections: Array<{code: string, title: string, path: string}>}>}
 *
 * Поле sections — разделы, ВЫДАННЫЕ роли, в порядке показа (техспека
 * BL-2026-10-08-a315 §5). Навигация строится из него, а не из перечня
 * в коде: состав разделов правит администратор, и второй список в JSX
 * разошёлся бы с выдачей молча.
 *
 * Поля position и department — должность и отдел человека; на права они не
 * влияют (доступ даёт роль) и у заведённых прежде записей пусты.
 */
export function me() {
  return request("/api/me");
}

/** Список пользователей (доступно только роли admin). */
export function listUsers() {
  return request("/api/users");
}

/**
 * Состояние источников данных: свежесть, последние прогоны, поводы.
 *
 * @returns {Promise<{sources: Array, count: number, healthy: number}>}
 *
 * Свежесть ДАННЫХ и исход ПРОГОНА приходят раздельно и означают разное:
 * коннектор может отрабатывать штатно, пока источник не обновляется.
 */
export function listSources() {
  return request("/api/sources");
}

/**
 * Внеплановый запуск задания загрузки.
 *
 * @param {string} code — код источника
 * @param {string} job — имя задания
 * @returns {Promise<object>} подтверждение приёма
 *
 * Не ждёт прогона: ответ приходит сразу, результат появляется
 * в состоянии источника, когда прогон закончится.
 */
export function runSourceJob(code, job) {
  return request(
    `/api/sources/${encodeURIComponent(code)}/jobs/${encodeURIComponent(job)}/run`,
    {
      method: "POST",
    },
  );
}

/**
 * Сводная картина казначейства: остатки, перекрытие, свободная ликвидность.
 *
 * @returns {Promise<object>} три блока картины, состояние источников и признак полноты
 *
 * Одна ручка, а не четыре запроса: показатели собираются на СЕРВЕРЕ и на
 * один момент времени. Четыре независимых вызова дали бы остатки на одну
 * минуту рядом с перекрытием на другую, а привязка «показатель → источники»
 * (BR-023) превратилась бы во вторую копию доменного правила здесь, в JS.
 */
export function loadDashboard() {
  return request("/api/treasury/dashboard");
}

/**
 * Скачивает выгрузку отчёта в Excel.
 *
 * @param {string} path — путь ручки, отдающей файл (например
 *        /api/treasury/dashboard.xlsx)
 * @returns {Promise<string>} имя сохранённого файла
 * @throws {Error} с текстом из поля detail — отказ приходит JSON-ом,
 *         а не книгой, и его нужно показать словами
 *
 * Побочные эффекты: создаётся временная ссылка на объект в памяти браузера
 * и по ней происходит переход, после чего ссылка освобождается.
 *
 * Отдельно от request(): та разбирает ответ как JSON, а здесь тело —
 * двоичный файл. И обычной ссылкой <a href> обойтись нельзя: браузер не
 * приложит к ней заголовок с токеном, и вместо файла придёт 401.
 */
export async function downloadReport(path) {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(path, { headers });
  if (response.status === 401) setToken(null);
  if (!response.ok) {
    const text = await response.text();
    let detail = `Ошибка ${response.status}`;
    try {
      detail = JSON.parse(text).detail || detail;
    } catch {
      // Отказ пришёл не JSON-ом — показываем код, а не пустую строку:
      // «ничего не произошло» худший из возможных ответов на нажатие.
    }
    throw new Error(detail);
  }

  // Имя файла задаёт сервер: оно содержит дату среза, и придумывать его
  // заново в браузере значило бы завести второй источник имени.
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const name = match ? match[1] : "report.xlsx";

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return name;
}

/**
 * Остатки кошельков платёжных систем.
 *
 * @param {string} source — код платёжной системы; пусто — все
 * @returns {Promise<{wallets: Array, totals: Array, sources: Array}>}
 *
 * Итоги приходят с сервера: сумма по валюте — доменная величина, и
 * посчитанная здесь она разошлась бы с выгрузкой и расчётами молча.
 * Валюты между собой НЕ складываются: приведение к базовой опирается
 * на курс, вводимый вручную.
 */
export function loadPspBalances(source = "") {
  const q = source ? `?source=${encodeURIComponent(source)}` : "";
  return request(`/api/treasury/psp-balances${q}`);
}

/**
 * Движения платёжных систем.
 *
 * @param {object} filters — { source, direction, status, matched, from, to, limit, offset }
 * @returns {Promise<{transactions: Array, total: number, totals: Array, unmatched: number}>}
 *
 * Постранично: движений тысячи в сутки, и выдача без предела однажды
 * вернула бы их все.
 */
export function loadPspTransactions(filters = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== "" && v !== null && v !== undefined) q.set(k, String(v));
  }
  const suffix = q.toString() ? `?${q}` : "";
  return request(`/api/treasury/psp-transactions${suffix}`);
}

/**
 * Сводка движений платёжных систем по календарным периодам.
 *
 * @returns {Promise<{generated_at: string, bounds: Array, counts: Array, sources: Array}>}
 *
 * Параметров нет: периоды — сутки, неделя и месяц — заданы календарём,
 * а не запросом. Счёт идёт на сервере: ручка движений отдаёт страницу,
 * и сложенная здесь страница называлась бы итогом за месяц, ничем
 * не отличаясь от него на глаз.
 */
export function loadPspFlowStats() {
  return request("/api/treasury/psp-flow-stats");
}

/**
 * Реестр клиентов платёжных систем.
 *
 * @param {object} filters — { source, matched, limit }
 * @returns {Promise<{customers: Array, count: number}>}
 */
export function loadPspCustomers(filters = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== "" && v !== null && v !== undefined) q.set(k, String(v));
  }
  const suffix = q.toString() ? `?${q}` : "";
  return request(`/api/treasury/psp-customers${suffix}`);
}

/**
 * Привязка клиента платёжной системы к клиенту системы.
 *
 * @param {string} source — код платёжной системы
 * @param {string} ref — идентификатор клиента у провайдера
 * @param {string} clientCode — код клиента системы; ПУСТАЯ строка снимает привязку
 * @returns {Promise<{transactions_linked: number}>}
 *
 * Ответ несёт число движений, которым проставлен клиент, — включая
 * загруженные ДО привязки: связь достаёт историю, а не только будущее.
 */
export function bindPspCustomer(source, ref, clientCode) {
  return request(
    `/api/treasury/psp-customers/${encodeURIComponent(source)}/${encodeURIComponent(ref)}/client`,
    { method: "PUT", body: { client_code: clientCode } },
  );
}

/**
 * Оценка криптоактивов на конец суток платёжного контура.
 *
 * @param {string} minDeviationPct — порог показа расхождения курса, в процентах;
 *                                   пусто — умолчание сервера (1 %)
 * @returns {Promise<{trade_date: string|null, wallets: Array, total_usd: string,
 *                    is_complete: boolean, reasons: Array<string>,
 *                    manual_entry_usd: string|null, manual_entry_diff: string|null,
 *                    rate_divergences: Array}>}
 *
 * Итог и признак полноты приходят С СЕРВЕРА и здесь не досчитываются:
 * кошелёк без курса в сумму не входит и нулём не считается (BR-010, BR-011),
 * а правило «что считается неполным» обязано быть одно на выгрузку, расчёт
 * и экран.
 */
export function loadCryptoValuation(minDeviationPct = "") {
  const q =
    minDeviationPct === ""
      ? ""
      : `?min_deviation_pct=${encodeURIComponent(minDeviationPct)}`;
  return request(`/api/treasury/crypto-valuation${q}`);
}

/**
 * Ввод ручного курса закрытия торгового дня.
 *
 * @param {object} rate — { currency_code, calendar_code, trade_date, rate, comment }
 * @returns {Promise<{currency_code: string, rate: string, converted_rows: number}>}
 *
 * Автор берётся сервером из токена, а не отправляется отсюда: имя, присланное
 * клиентом, подписало бы чужой ввод чьим угодно именем.
 */
export function createManualFxRate(rate) {
  return request("/api/treasury/fx-rates/manual", {
    method: "POST",
    body: rate,
  });
}

/**
 * Расхождения сверки платёжных систем с дилингом.
 *
 * @param {boolean} onlyOpen — только неразобранные
 * @returns {Promise<{breaks: Array, count: number}>}
 *
 * Перечень приходит с сервера уже отсортированным и ограниченным: правило
 * «что считается расхождением» обязано быть одно на прогон, выгрузку и экран.
 */
export function loadReconBreaks(onlyOpen = true) {
  return request(
    `/api/treasury/recon-breaks?only_open=${onlyOpen ? "true" : "false"}`,
  );
}

/**
 * Закрытие расхождения с причиной.
 *
 * @param {number} id — расхождение
 * @param {string} status — 'resolved' (разобрано) либо 'not_applicable' (сверять было нечего)
 * @param {string} resolution — причина; обязательна
 * @returns {Promise<{id: number, status: string, resolved_by: string}>}
 *
 * Автор берётся сервером из токена, а не отправляется отсюда: имя,
 * присланное клиентом, подписало бы чужое решение чьим угодно именем.
 */
export function resolveReconBreak(id, status, resolution) {
  return request(`/api/treasury/recon-breaks/${id}/resolve`, {
    method: "POST",
    body: { status, resolution },
  });
}

/**
 * Значения выпадающих списков раздела Dealing Desk.
 *
 * @returns {Promise<{groups: Array<string>, countries: Array<string>,
 *                    states: Array<string>, agents: Array<string>,
 *                    currencies: Array<string>, as_of: string, stale: boolean}>}
 *
 * Списки приходят С СЕРВЕРА, а не собираются из загруженных строк: показать
 * в фильтре надо всё, что видит менеджерский доступ, включая срезы, где за
 * выбранный период не оказалось ни одной строки. Список, собранный из
 * выдачи, сузился бы ровно там, где отбор и нужен. Правила «Ядра партнёрки»
 * применены на сервере (техспека §6.1): отфильтрованная сущность не должна
 * попадать ни в таблицы, ни в списки.
 */
export function loadDealingFilters() {
  return request("/api/dealing/filters");
}

/**
 * Строки витрины Dealing Desk по одной книге.
 *
 * @param {string} book — 'bbook' | 'abook' | 'cent_bbook'
 * @param {object} query — остальные параметры отбора: from, to, login,
 *        country, state, agent, group, currency (мультивыбор — через запятую),
 *        nbp, to_usd
 * @returns {Promise<{book: string, from: string, to: string, rows: Array,
 *                    totals: object, as_of: string, stale: boolean}>}
 *
 * Запрос НА КАЖДУЮ книгу свой, а не один на три таблицы: книги считаются
 * независимо и отвечают с разной скоростью, и одна медленная задержала бы
 * показ двух готовых.
 *
 * Денежные поля в ответе — СТРОКИ, и такими они и остаются до экрана:
 * JSON-число проходит через double браузера, а раскладка по книгам обязана
 * сходиться до копейки (Приложение А техспеки BL-2026-09-16-90e6).
 *
 * Итоги (totals) приходят с сервера: сумма по книге — доменная величина,
 * и посчитанная здесь она разошлась бы с выгрузкой и расчётом молча.
 */
export function loadDealingRows(book, query = {}) {
  const q = new URLSearchParams({ book });
  for (const [k, v] of Object.entries(query)) {
    if (v !== "" && v !== null && v !== undefined) q.set(k, String(v));
  }
  return request(`/api/dealing/rows?${q}`);
}

/**
 * Матрица приоритетов перекрытия — чтение (BRD §4, Приложение А техспеки
 * BL-2026-09-16-90e6).
 *
 * @returns {Promise<{rules: Array<{id: number, priority: number,
 *          login: string|null, group: string|null, symbol: string|null,
 *          symbol_enabled: boolean, a_book_pct: string,
 *          updated_at: string, updated_by: string}>, can_edit: boolean}>}
 *
 * Чтение открыто всем, кому выдан раздел Dealing Desk; правка — ролям,
 * названным предикатом CanConfigureDealing на сервере.
 *
 * ЗАЧЕМ В ОТВЕТЕ `can_edit`. Чтобы интерфейс не выводил право из названия
 * роли. Выведенное здесь, оно стало бы третьей копией решения о правах —
 * после предиката в Go и перечня маршрутов, — и разошлось бы с ними при
 * первом же изменении состава ролей, причём молча: человек увидел бы
 * правимые поля и получил 403 на сохранение. Признак приходит оттуда же,
 * откуда пришёл бы отказ. Образец — поле `locked` в матрице выдачи
 * разделов (GET /api/access/sections).
 *
 * `login` приходит СТРОКОЙ, а не числом, хотя пример Приложения А записан
 * числом: внешний логин платформы — это идентификатор, а не величина, и
 * счёт `0900518` не равен счёту `900518`. Контракт сервера (поле *string)
 * здесь и есть источник истины.
 *
 * `a_book_pct` — доля A-Book ПРОЦЕНТОМ («80.00»), а не долей 0..1. Долями
 * она хранится в схеме, и перевод делает сервер (ParsePercent): в единицах
 * 1e-6 процента и 1e-8 доли это одно и то же число, поэтому перевод
 * обходится без деления и без округления. Отправить сюда «0.8» вместо
 * «80.00» означало бы задать перекрытие в сто двадцать пять раз меньше —
 * и сервер принял бы это молча, потому что 0.8 % в диапазон [0; 100] входит.
 */
export function loadDealingCoverageRules() {
  return request("/api/dealing/coverage-rules");
}

/**
 * Матрица приоритетов перекрытия — запись ЦЕЛИКОМ.
 *
 * @param {Array<object>} rules — полный желаемый состав матрицы; каждое
 *        правило: { priority, login, group, symbol, symbol_enabled,
 *        a_book_pct } (процент строкой, см. loadDealingCoverageRules)
 * @returns {Promise<{rules: Array<object>}>} матрица после правки
 *
 * Побочный эффект на сервере: правка dealing.coverage_rule и запись
 * dealing.coverage_snapshot ОДНОЙ транзакцией.
 *
 * ПОЧЕМУ СПИСОК ЦЕЛИКОМ И ЧЕМ ЭТО ОПАСНО. Матрица — это порядок приоритетов,
 * и «снять правило» меняет действующую долю ровно так же, как «изменить
 * процент»: после снятия начинает действовать уровень ниже. Поэтому сервер
 * принимает желаемый состав целиком и сам считает разницу. Цена та же, что
 * у матрицы выдачи разделов: правило, не попавшее в тело, будет УДАЛЕНО, и
 * удаление тоже уйдёт в историю снепшотом. Отсюда требование к вызывающему:
 * присылать все правила, а не изменённые.
 *
 * Автор правки берётся сервером из токена, а не из тела: имя, присланное
 * клиентом, подписало бы чужую правку чьим угодно именем.
 */
export function saveDealingCoverageRules(rules) {
  return request("/api/dealing/coverage-rules", {
    method: "PUT",
    body: { rules },
  });
}

/**
 * Каталог универсальных символов и их суффиксов (§4 техспеки
 * BL-2026-10-09-4f80).
 *
 * @returns {Promise<{symbols: Array<{code: string, contract_size: string,
 *          digits: number, aliases: Array<string>, manual: number,
 *          auto: number}>, unbound: Array<string>, as_of: string}>}
 *
 * Нужен матрице: символ правила — УНИВЕРСАЛЬНЫЙ (`EURUSD`), а не суффикс
 * группы (`EURUSD.g`), и выбирать его из списка, а не вписывать руками,
 * — единственный способ не завести правило на символ, которого нет.
 *
 * Поле `unbound` — суффиксы, которым универсальный символ не подобран. Это
 * не ошибка загрузки, а перечень для разбора, и он обязан быть виден: молча
 * отнесённый к догадке суффикс означал бы объём, посчитанный неизвестно чей.
 */
export function loadDealingSymbols() {
  return request("/api/dealing/symbols");
}

/**
 * Разрешение иерархии: что применится к этому счёту по этому символу и почему.
 *
 * @param {string} login — внешний логин счёта
 * @param {string} symbol — универсальный символ; пустая строка означает
 *        вопрос «что применится к логину целиком, без разбивки по символам»
 * @returns {Promise<{login: string, symbol: string,
 *          effective: {a_book_pct: string, source: string, priority: number},
 *          considered: Array<{priority: number, matched: boolean,
 *          scope: string, a_book_pct?: string, why?: string}>}>}
 *
 * Поле `considered` отдаёт И ПРОИГРАВШИЕ правила с причиной. Это и есть
 * предмет ручки: победителя можно было бы вычислить и из списка правил,
 * но «почему проиграли остальные» из трёх отдельных списков читатель
 * выводит сам — и ошибается ровно там, где иерархия сложнее всего.
 */
export function resolveDealingCoverage(login, symbol) {
  const q = new URLSearchParams({ login });
  if (symbol) q.set("symbol", symbol);
  return request(`/api/dealing/coverage-resolve?${q}`);
}

/**
 * Перечень клиентов, из которых сложилась величина карточки книги.
 *
 * @param {string} book — 'a_book' | 'b_book'
 * @param {string} platform — 'unity' | 'mt5' либо '' (все платформы)
 * @returns {Promise<{book: string, book_title: string, platform: string,
 *                    rows: Array, total_usd: string, scope_total_usd: string,
 *                    without_client_usd: string,
 *                    without_client_providers: Array<string>, clients: number}>}
 *
 * Запрос уходит по щелчку по карточке, а не вместе с дашбордом: перечень
 * бывает в двести пятьдесят строк, и грузить его тем, кто карточку не
 * открывал, значило бы платить за каждый опрос дашборда.
 *
 * Денежные поля — СТРОКИ и такими остаются до экрана: сумма перечня обязана
 * сходиться с карточкой до копейки, а JSON-число прошло бы через double.
 */
export function loadBookClients(book, platform = "") {
  const q = new URLSearchParams({ book });
  if (platform) q.set("platform", platform);
  return request(`/api/treasury/book-clients?${q}`);
}

/**
 * Заводит учётную запись (только администратор).
 *
 * @param {object} user — { username, full_name, position, department, role,
 *        password }
 * @returns {Promise<object>} заведённая запись (без пароля и без хеша)
 * @throws {Error} с текстом из поля detail: короткий логин, короткий пароль,
 *         неизвестная роль, занятый логин
 *
 * Побочный эффект на сервере: INSERT app.users + запись в журнал изменений.
 *
 * Пароль уходит ОДИН раз и назад не приходит: в ответе его нет, в списке
 * записей нет, и восстановить его нечем. Поэтому показать человеку его
 * обязан тот экран, с которого запись заводили (техспека §5.1).
 */
export function createUser(user) {
  return request("/api/users", { method: "POST", body: user });
}

/**
 * Правит реквизиты и роль учётной записи.
 *
 * @param {number} id — учётная запись
 * @param {object} patch — любое подмножество { full_name, position,
 *        department, role }; отсутствующее поле НЕ меняется
 * @returns {Promise<object>} запись после правки
 * @throws {Error} с текстом из поля detail: снятие последнего активного
 *         администратора, неизвестная роль, запись не найдена
 *
 * Побочные эффекты на сервере: UPDATE app.users, запись в журнал изменений
 * и СБРОС расчёта разделов — смена роли меняет то, что человек видит, без
 * выкладки (техспека §6 п. 3).
 *
 * PATCH, а не PUT: это правка, а не замена. PUT обязал бы экран присылать
 * все поля целиком, и поле, забытое в теле, обнулило бы себя молча — та же
 * ошибка, что уже случилась с матрицей выдачи (§5.2).
 */
export function updateUser(id, patch) {
  return request(`/api/users/${id}`, { method: "PATCH", body: patch });
}

/**
 * Меняет пароль учётной записи (администратором).
 *
 * @param {number} id — учётная запись
 * @param {string} password — новый пароль; проверка длины — на сервере,
 *        та же, что при заведении
 * @returns {Promise<object>} подтверждение
 * @throws {Error} с текстом из поля detail: короткий пароль, запись не найдена
 *
 * Побочный эффект на сервере: UPDATE хеша пароля + запись в журнал изменений.
 *
 * Пароль в ответе не возвращается: его знает только тот, кто отправил
 * запрос, — то есть этот экран, и показать он обязан его сам.
 */
export function setUserPassword(id, password) {
  return request(`/api/users/${id}/password`, {
    method: "POST",
    body: { password },
  });
}

/**
 * Включает или отключает учётную запись.
 *
 * @param {number} id — учётная запись
 * @param {boolean} active — true включает, false отключает
 * @returns {Promise<object>} запись после изменения
 * @throws {Error} с текстом из поля detail — в том числе «нельзя отключить
 *         самого себя» и «нельзя снять последнего администратора»
 *
 * Побочный эффект на сервере: UPDATE app.users.is_active + журнал изменений.
 *
 * Записи не удаляются, а отключаются: на них ссылается журнал входов, и
 * удаление обнулило бы историю действий человека.
 */
export function setUserActive(id, active) {
  return request(`/api/users/${id}/${active ? "activate" : "deactivate"}`, {
    method: "POST",
  });
}

/**
 * Каталог разделов и матрица выдачи «роль × раздел» (администратору).
 *
 * @returns {Promise<{sections: Array<{code: string, title: string,
 *                    path: string, sort_order: number}>,
 *                    roles: Array<{role: string, sections: Array<string>,
 *                    locked: boolean}>}>}
 *
 * Матрица приходит по ВСЕМ ролям модели, включая те, кому не выдано ничего:
 * роль без строки выглядела бы отсутствующей, и администратор не нашёл бы,
 * кому выдавать.
 *
 * Признак locked (он стоит у admin) — часть контракта, а не правило,
 * известное одному фронтенду: экран обязан показать строку нередактируемой,
 * а не дать нажать и получить отказ (техспека BL-2026-10-08-a315 §5).
 */
export function loadAccessSections() {
  return request("/api/access/sections");
}

/**
 * Заменяет матрицу выдачи «роль × раздел» ЦЕЛИКОМ.
 *
 * @param {Array<{role: string, sections: Array<string>}>} roles — ПОЛНАЯ
 *        матрица по всем правимым ролям; строка admin в теле недопустима
 * @returns {Promise<object>} новая матрица — тем же телом, что отдаёт GET
 * @throws {Error} с текстом из поля detail: роль admin в теле, неизвестная
 *         роль, неизвестный раздел
 *
 * Побочные эффекты на сервере: DELETE + INSERT app.role_section в одной
 * транзакции, запись в журнал изменений и сброс кэша выдачи.
 *
 * ПОЧЕМУ МАТРИЦА ЦЕЛИКОМ — И ЧЕМ ЭТО ОПАСНО. «Кому какие разделы открыты» —
 * одно решение администратора, и применяется он одним шагом. Цена: роль,
 * ОТСУТСТВУЮЩАЯ в теле, теряет все свои разделы. При живой проверке
 * карточки BL-2026-10-08-a315 частичный PUT обнулил выдачу трёх ролей, и
 * заметно это стало только по следующему запросу. Отсюда требование к
 * вызывающему: присылать все правимые роли, а не изменённые строки.
 *
 * Строка admin в тело НЕ попадает: её выдача не хранится и неотзываема
 * (§3.3 той спеки), и сервер отвергает тело, в котором она есть.
 */
export function saveAccessSections(roles) {
  return request("/api/access/sections", { method: "PUT", body: { roles } });
}

/* --------------------------------------------------------------------------
   Банковские выписки (карточка BL-2026-08-15-2024, техспека §6, §7.2).

   Суммы во всех ответах — СТРОКИ decimal до точности валюты, и такими они
   остаются до экрана: арифметики над ними здесь нет, только показ.
   -------------------------------------------------------------------------- */

/**
 * Собирает строку запроса из отбора, пропуская пустые значения.
 *
 * @param {object} filters — пары «параметр → значение»
 * @returns {string} «?a=1&b=2» либо пустая строка
 *
 * Пустое значение не уходит вовсе: `?bank=` сервер прочёл бы как «банк с
 * пустым кодом», а не как «любой банк».
 */
function queryString(filters = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== "" && v !== null && v !== undefined) q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/**
 * Перечень разборщиков выписок — для выбора банка при загрузке (§6.1).
 *
 * @returns {Promise<{parsers: Array<{code: string, title: string, version: string}>}>}
 *
 * Перечень приходит с сервера, а не задан здесь: новый банк добавляется
 * разборщиком на сервере (критерий приёмки 3), и список в коде фронтенда
 * отстал бы от него до следующей выкладки.
 */
export function loadBankParsers() {
  return request("/api/treasury/bank-statements/parsers");
}

/**
 * Загружает PDF-выписку (§6.2).
 *
 * @param {File|Blob} file — PDF не больше 5 МиБ
 * @param {string} bank — код разборщика из loadBankParsers; пусто —
 *        автоопределение
 * @returns {Promise<{status: number, ok: boolean, data: object}>} код ответа
 *          и тело. 201 — карточка загрузки, 200 — дубликат со ссылкой на
 *          исходную (`duplicate_of`), 4xx/5xx — `{detail, …}`
 * @throws {Error} только при сетевом отказе — ответа сервера нет вовсе
 *
 * Побочный эффект: при 401 токен удаляется (как в request()).
 *
 * # ПОЧЕМУ СВОЙ fetch, А НЕ request()
 *
 * 1. Тело — multipart/form-data. Заголовок Content-Type здесь НЕ ставится:
 *    его вместе с boundary ставит сам браузер, и заданный руками
 *    `multipart/form-data` без boundary сервер разобрать не смог бы.
 * 2. request() на 4xx бросает только текст `detail`, а экрану загрузки нужно
 *    больше: у 422 — `upload_id`, `reason` и `candidates` (признаки, по
 *    которым разборщики узнали или не узнали файл). Без них подсказка
 *    «выберите банк явно» была бы голословной.
 * 3. 200 и 201 — РАЗНЫЕ исходы (дубликат и разбор), и вызывающему нужен код.
 *
 * Тело ответа разбирается осторожно: 413 от nginx (файл больше 8 МиБ)
 * приходит HTML-страницей, а не JSON, — тогда `detail` подставляется здесь,
 * чтобы экран всё равно назвал причину словами.
 */
export async function uploadBankStatement(file, bank = "") {
  const form = new FormData();
  form.append("file", file);
  if (bank) form.append("bank", bank);

  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch("/api/treasury/bank-statements", {
    method: "POST",
    headers,
    body: form,
  });
  if (response.status === 401) setToken(null);

  const text = await response.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {
      detail:
        response.status === 413
          ? "файл слишком большой — сервер его не принял"
          : `Ошибка ${response.status}`,
    };
  }
  if (!response.ok && !data.detail) data.detail = `Ошибка ${response.status}`;
  return { status: response.status, ok: response.ok, data };
}

/**
 * Список загрузок выписок (§6.3).
 *
 * @param {object} filters — { bank, status, limit, offset }
 * @returns {Promise<{uploads: Array, total: number}>}
 *
 * Список включает и неудачные загрузки (`failed`, `duplicate`): журнал
 * загрузок — это ответ на вопрос «что приносили», а не только «что приняли».
 */
export function loadBankStatements(filters = {}) {
  return request(`/api/treasury/bank-statements${queryString(filters)}`);
}

/**
 * Карточка загрузки: шапка, счета с итогами «банк vs разбор», нераспознанные
 * строки (§6.4).
 *
 * @param {number} id — загрузка
 * @returns {Promise<{upload: object, duplicate_of: object|null,
 *                    accounts: Array, unrecognized: Array,
 *                    unrecognized_truncated?: boolean}>}
 */
export function loadBankStatement(id) {
  return request(`/api/treasury/bank-statements/${encodeURIComponent(id)}`);
}

/** Размер страницы операций загрузки — умолчание сервера (§6.5). */
export const BANK_ENTRIES_PAGE = 200;

/**
 * Операции загрузки по одному счёту с исходом (§6.5).
 *
 * @param {number} id — загрузка
 * @param {number} statementAccountId — раздел счёта в загрузке (обязателен:
 *        операции показываются по счёту)
 * @param {number} page — номер страницы с нуля; страница — 200 строк
 * @returns {Promise<{entries: Array, total: number}>}
 */
export function loadBankStatementEntries(id, statementAccountId, page = 0) {
  const limit = BANK_ENTRIES_PAGE;
  return request(
    `/api/treasury/bank-statements/${encodeURIComponent(id)}/entries${queryString({
      statement_account_id: statementAccountId,
      limit,
      offset: page * limit,
    })}`,
  );
}

/**
 * Операции по банковскому счёту или банку (§6.6).
 *
 * @param {object} filters — { account_id, bank, kind, from, to, limit, offset }
 * @returns {Promise<{transactions: Array, total: number, totals: Array}>}
 *
 * Итоги по валютам приходят с сервера: сумма по отбору — доменная величина,
 * а страница — не весь отбор.
 */
export function loadBankTransactions(filters = {}) {
  return request(`/api/treasury/bank-transactions${queryString(filters)}`);
}

/**
 * Справочник банковских счетов с последним остатком (§6.7).
 *
 * @returns {Promise<{accounts: Array}>}
 *
 * `balance: null` — по счёту не принято ни одной выписки; это НЕ ноль
 * (BR-011), и показывать его нужно словами.
 */
export function loadBankAccounts() {
  return request("/api/treasury/bank-accounts");
}

/**
 * Разметка принадлежности денег на банковском счёте (§6.8, только admin).
 *
 * @param {number} id — банковский счёт (account_id)
 * @param {string} owner — 'company' | 'client' | 'unknown'
 * @param {string} comment — основание; обязательно (400 при пустом)
 * @returns {Promise<object>} счёт после правки — форма §6.7
 *
 * Побочный эффект на сервере: запись в журнал изменений и закрытие находки
 * «счёт не размечен». Автор берётся из токена, а не из тела.
 */
export function setBankAccountOwner(id, owner, comment) {
  return request(
    `/api/treasury/bank-accounts/${encodeURIComponent(id)}/funds-owner`,
    { method: "PUT", body: { funds_owner: owner, comment } },
  );
}

/**
 * Итоги банковских счетов для блока «Общий баланс Bank» (§6.9).
 *
 * @param {string} at — момент RFC 3339; пусто — «сейчас»
 * @returns {Promise<object>} счета, свод по валютам, долларовые итоги
 *          собственных и клиентских средств и причины неполноты
 *
 * `own_total_usd` / `client_total_usd` = null означает «итога НЕТ» (валюта
 * без курса, BR-010), а не ноль.
 */
export function loadBankBalances(at = "") {
  return request(`/api/treasury/bank-balances${queryString({ at })}`);
}
